import { FormEvent, useCallback, useRef } from 'react'
import { FormState, UseFormTrigger } from 'react-hook-form'

import { ShotlistsService } from '@/services/shotlist.service'

// TODO: make the code below available, when we need upload two platforms together - English and Spanish
// Do not delete it!!!
// import { UploadFormService } from '@/services/uploadForm.service'
import { PLATFORMS_CODES, PLATFORMS_LABELS } from '@/constants/languages'
import {
	IPlatform,
	ISelectedPlatform,
	IUploaderForm,
	TPlatformLabel,
	TPlatformValue
} from '@/types/uploadForm.interface'
import { toastError } from '@/utils/toastError'

interface IUseFormContentSubmitProps {
	taskId: string
	selectedPlatform: TPlatformValue | null
	selectedPlatformList?: ISelectedPlatform[]
	platformList?: IPlatform[] | null
	toFollow: boolean
	formState: FormState<IUploaderForm>
	trigger: UseFormTrigger<IUploaderForm>
	onSubmit: () => Promise<void>
}

export const useFormContentSubmit = ({
	taskId,
	selectedPlatform,
	selectedPlatformList,
	platformList,
	toFollow,
	formState,
	trigger,
	onSubmit
}: IUseFormContentSubmitProps) => {
	const isSubmittingRef = useRef(false)

	const handleFormSubmit = useCallback(
		async (e: FormEvent) => {
			e.preventDefault()

			if (isSubmittingRef.current) return
			isSubmittingRef.current = true

			try {
				const shotlist = await ShotlistsService.getShotlist(taskId)

				const shotlistLanguages = [
					shotlist?.original?.language,
					...(shotlist?.translations?.map(tran => tran.language) || [])
				]

				const emptyPlatforms =
					selectedPlatformList
						.map(plat => plat.value)
						.filter(
							plat => !shotlistLanguages.includes(PLATFORMS_CODES[plat])
						) || []

				// TODO: make the code below available, when we need upload two platforms together - English and Spanish
				// Do not delete it!!!

				// const form = await UploadFormService.getFormData(taskId)

				// const isArabic = form?.platforms_order?.[0] === 'arabic'

				// const availablePlatforms = form?.platforms || {}

				// const REQUIRED_PLATFORMS: TPlatformValue[] = ['english', 'spanish']

				// const missedPlatforms = REQUIRED_PLATFORMS.filter(
				// 	plat => !(plat in availablePlatforms)
				// )

				if (!toFollow && emptyPlatforms.length > 0) {
					const platformsString = emptyPlatforms
						.map(plat => PLATFORMS_LABELS[plat])
						.join(', ')

					toastError(
						`You have to add transcription or translation for ${platformsString} platform!`
					)

					return
				}

				// TODO: make the code below available, when we need upload two platforms together - English and Spanish
				// Do not delete it!!!

				// if (!isArabic && missedPlatforms.length > 0) {
				// 	const platformsString = missedPlatforms
				// 		.map(plat => PLATFORMS_LABELS[plat])
				// 		.join(', ')

				// 	toastError(`You have to add ${platformsString} platform!`)

				// 	return
				// }

				const isValid = await trigger()

				if (!isValid) {
					const { errors } = formState
					const errorFields = Object.keys(errors) as Array<keyof IUploaderForm>

					if (errorFields.length > 0) {
						const firstErrorField = errorFields[0]
						const fieldElement = document.querySelector(
							`[data-field-name="${firstErrorField}"]`
						) as HTMLElement

						if (fieldElement) {
							fieldElement.scrollIntoView({
								behavior: 'smooth',
								block: 'center'
							})
							setTimeout(() => {
								const inputElement = fieldElement.querySelector(
									'input, textarea, [role="combobox"], [role="listbox"]'
								) as HTMLElement
								if (inputElement) {
									inputElement.focus()
								}
							}, 300)
						}
					}
					return
				}

				await onSubmit()
			} catch (error) {
				console.log('Form submit failed:', error)
				toastError('Submitting error. Please try again.')
			} finally {
				isSubmittingRef.current = false
			}
		},
		[taskId, selectedPlatformList, toFollow, trigger, formState, onSubmit]
	)

	const currentPlatform = selectedPlatform
		? selectedPlatformList?.find(p => p.value === selectedPlatform) || null
		: null

	const selectedPlatformLabel: TPlatformLabel | '' = selectedPlatform
		? platformList?.find(p => p.value === selectedPlatform)?.label || ''
		: ''

	const isShowFields = selectedPlatform === selectedPlatformList?.[0]?.value

	return {
		handleFormSubmit,
		currentPlatform,
		selectedPlatformLabel,
		isShowFields
	}
}
